import type { HistoryEntry } from '@ctcells/contract'
import { t, type MessageKey } from '@ctcells/i18n'

import { fullDateTimeText } from '../format.ts'

/**
 * 작업별 이벤트 로그 CSV — 「이벤트」 원장 셋(`GET /history?event_type=event|batch_record|audit&batch_id=`)을 끝까지 읽은 행을
 * 파일 한 장으로(eric 2026-10-02 · `@ctcells/api` `fetchAllEventHistory`).
 *
 * 🔴 **감사 원장도 싣는다**(팀장 결정 2026-10-11 · `@ctcells/api` `BATCH_EVENT_LEDGERS`) — 사람이 누른 시작 · 중지는 거기에만
 *    있다. 감사 행은 `actor`(누가) · `detail{action, operation, reason}` 이다. 명령 원장은 받지 않는다.
 * 🔴 **2026-10-08(v0.9.4) 배치 기록이 `event_log` 에서 `batch_record` 로 옮겨 갔다** — 옛 작업은 `event`, 새 작업은
 *    `batch_record` 에 있어 둘을 섞어 싣는다. 그래서 **「원장」 열**(`event_type` 원값)을 둔다 — 행마다 갈린다.
 * 🔴 **열은 응답 칸 그대로다.** 두 원장의 `detail` 은 서버가 고정으로 짓는다(`app/services/history.py`):
 *    - `event` → `category, operation, error_code, severity, context, error_message, reason, correlation_id`
 *    - `batch_record` → `kind, category, error_code, severity, module_id, execution_id, command_id, context, reason, correlation_id`
 *    `batch_record.kind` 는 종전 `event_log.operation` 낱말 그대로라(서버 `batch_dispatch._write_event`) 「동작」 한 열에
 *    원장별로 읽는다(`EVENT_DETAIL_COLUMNS` 의 `batchRecordKey`). 그 밖은 같은 이름 같은 열이다 — 그 원장에 없는 칸은 빈 칸.
 *    화면처럼 「내용」을 풀어 쓰지 않는다 — 내려받은 파일은 «기록»이라 해석이 들어가면 원장과 달라진다.
 *    `context` 는 자유 객체라 JSON 한 칸이다.
 *    - 「행위자」 열은 행의 `actor` 다 — 감사 행만 차고 `event` · `batch_record` 행은 늘 빈 칸이다(서버가 키를 뺀다)
 *    - `target` 열은 **없다** — `batch_id` 로 거르므로 **늘 `batch:<이 작업>`** 이다 — 머리 줄의 작업 ID 와 같은 말이다
 *    - 「시각」은 같은 `ts` 를 현장 시간대(`DISPLAY_TIME_ZONE`)로 읽은 것이다. 원본(`ts`)도 옆 칸에 둔다
 *      (초 아래 · 시간대 오프셋이 거기만 남는다)
 *    - 서버가 `detail` 에 **모르는 키를 더 실으면** 버리지 않고 「그 밖」 열 하나에 JSON 으로 싣는다.
 *      그 열은 그런 행이 있을 때만 선다
 *    - 머리 몇 줄(작업 이름 · ID · 상태 · 기준 시각 · 행 수) — 도는 작업은 «누른 시각까지»라 그 시각을 파일이 말해야 한다
 * 🔴 **차례는 오래된 것부터**다(시간순). 서버는 `ts desc` 로 준다 — 뒤집은 뒤 시각으로 안정 정렬한다.
 */

export interface EventLogMeta {
  /** 사람에게 부르는 이름(`batchDisplayName`) */
  batchName: string
  batchId: string
  /** 상태 표기(사전 문구) */
  statusLabel: string
  /** 누른 시각 = 서버에 보낸 `to` */
  asOf: Date
}

/** 엑셀이 UTF-8 로 열게 하는 표지(BOM). 없으면 한글이 깨진다 */
export const CSV_BOM = '﻿'
/** RFC 4180 줄끝 */
const CRLF = '\r\n'

/**
 * 두 원장 `detail` 의 **서버 고정 키**와 머리글 — 차례가 곧 열 차례다.
 * `batchRecordKey` 가 있으면 `batch_record` 행은 그 키를 읽는다(같은 뜻 · 다른 이름 — 「동작」 = `operation` / `kind`).
 * ⚠ 서버가 키를 바꾸면 여기 한 곳을 고친다. 모르는 키는 「그 밖」 열로 떨어져 사라지지 않는다.
 */
export const EVENT_DETAIL_COLUMNS: readonly {
  key: string
  batchRecordKey?: string
  header: MessageKey
}[] = [
  { key: 'severity', header: 'column.severity' },
  { key: 'category', header: 'column.kind' },
  { key: 'operation', batchRecordKey: 'kind', header: 'column.operation' },
  /* 감사 행만 — `audit_log.action`(`batch_control` · `abort` …) */
  { key: 'action', header: 'column.action' },
  { key: 'error_code', header: 'eventLog.csv.errorCode' },
  { key: 'error_message', header: 'eventLog.csv.errorMessage' },
  { key: 'reason', header: 'column.reason' },
  { key: 'module_id', header: 'column.moduleId' },
  { key: 'execution_id', header: 'eventLog.csv.executionId' },
  { key: 'command_id', header: 'eventLog.csv.commandId' },
  { key: 'correlation_id', header: 'column.correlationId' },
  { key: 'context', header: 'eventLog.csv.context' },
]

/** 그 행의 원장에서 그 열이 읽는 키 */
function detailKeyOf(entry: HistoryEntry, column: (typeof EVENT_DETAIL_COLUMNS)[number]): string {
  return entry.event_type === 'batch_record' ? (column.batchRecordKey ?? column.key) : column.key
}

/**
 * 한 칸. 쉼표 · 따옴표 · 줄바꿈 · 앞뒤 공백이 있으면 따옴표로 싸고 안의 따옴표는 두 번 쓴다.
 * 🔴 **수식 주입 막기** — `=` · `+` · `@` · 탭 · CR 로 시작하면 엑셀이 수식으로 읽는다. 앞에 `'` 를 붙인다
 *    (OWASP CSV Injection). `-` 는 붙이지 않는다 — 음수처럼 `-` 로 시작할 값이 올 수 있고 그 단독으로는 함수가 안 된다.
 */
export function csvCell(value: string): string {
  const guarded = /^[=+@\t\r]/.test(value) ? `'${value}` : value
  return /[",\r\n]|^\s|\s$/.test(guarded) ? `"${guarded.replaceAll('"', '""')}"` : guarded
}

function csvLine(cells: readonly string[]): string {
  return cells.map(csvCell).join(',')
}

/** 칸 값 — 문자열은 그대로, null · 없음은 빈 칸, 그 밖(객체 · 수)은 JSON */
function valueCell(value: unknown): string {
  if (value === undefined || value === null) return ''
  return typeof value === 'string' ? value : JSON.stringify(value)
}

/** 서버 `ts desc` → 오래된 것부터. 같은 시각은 서버가 준 차례의 반대(= 시간순)로 둔다 */
export function chronological(entries: readonly HistoryEntry[]): HistoryEntry[] {
  return [...entries]
    .reverse()
    .map((entry, index) => ({ entry, index, ms: Date.parse(entry.ts) }))
    .sort((a, b) => {
      const byTime = (Number.isNaN(a.ms) ? 0 : a.ms) - (Number.isNaN(b.ms) ? 0 : b.ms)
      return byTime === 0 ? a.index - b.index : byTime
    })
    .map(({ entry }) => entry)
}

/** `detail` 에서 고정 키를 뺀 나머지. 없으면 `undefined` */
function extraDetail(entry: HistoryEntry): Record<string, unknown> | undefined {
  // 🔴 원장별로 가른다 — `event` 행에 `kind` 가 실려 오면 그건 모르는 키다(「동작」 열은 `operation` 만 읽는다)
  const known = new Set(EVENT_DETAIL_COLUMNS.map((column) => detailKeyOf(entry, column)))
  const extra = Object.entries(entry.detail ?? {}).filter(([key]) => !known.has(key))
  return extra.length === 0 ? undefined : Object.fromEntries(extra)
}

/** 표 머리글 — 시각 둘 · 원장 · 행위자 · `detail` 고정 키 차례. 「그 밖」은 모르는 키가 실린 행이 있을 때만 */
export function eventLogHeader(withExtra: boolean): string[] {
  return [
    t('eventLog.csv.time'),
    t('eventLog.csv.isoTime'),
    t('eventLog.csv.ledger'),
    t('column.actor'),
    ...EVENT_DETAIL_COLUMNS.map((column) => t(column.header)),
    ...(withExtra ? [t('eventLog.csv.extra')] : []),
  ]
}

export function buildEventLogCsv(entries: readonly HistoryEntry[], meta: EventLogMeta): string {
  const ordered = chronological(entries)
  const extras = ordered.map(extraDetail)
  const withExtra = extras.some((extra) => extra !== undefined)
  const rows = ordered.map((entry, index) => [
    fullDateTimeText(entry.ts),
    entry.ts,
    entry.event_type,
    valueCell(entry.actor),
    ...EVENT_DETAIL_COLUMNS.map((column) => valueCell(entry.detail?.[detailKeyOf(entry, column)])),
    ...(withExtra ? [valueCell(extras[index])] : []),
  ])
  const head = [
    [t('eventLog.csv.batchName'), meta.batchName],
    [t('eventLog.csv.batchId'), meta.batchId],
    [t('eventLog.csv.status'), meta.statusLabel],
    [
      t('eventLog.csv.asOf'),
      t('eventLog.csv.asOfValue', { at: fullDateTimeText(meta.asOf.toISOString()) }),
    ],
    [t('eventLog.csv.rowCount'), String(rows.length)],
  ]
  const lines = [...head.map(csvLine), '', csvLine(eventLogHeader(withExtra)), ...rows.map(csvLine)]
  return CSV_BOM + lines.join(CRLF) + CRLF
}

/** 파일 이름에 못 쓰는 글자(윈도우 기준 · 제어 문자 포함) */
// eslint-disable-next-line no-control-regex -- 제어 문자를 걸러 내는 것이 이 식의 일이다
const UNSAFE_FILE_CHARS = /[\\/:*?"<>|\u0000-\u001f]/g

/**
 * `작업이름_이벤트로그_YYYYMMDD-HHmmss.csv` — 시각은 현장 시간대.
 * 🔴 **이름이 없으면(= 제목이 작업 ID 그대로면) 작업 ID 앞 8자**다(36자 UUID 를 파일 이름에 다 싣지 않는다).
 */
export function eventLogFileName(batchName: string, batchId: string, at: Date): string {
  const base = batchName === batchId || batchName.trim() === '' ? batchId.slice(0, 8) : batchName
  const safe = base.replace(UNSAFE_FILE_CHARS, '_').replace(/\s+/g, ' ').trim()
  const stamp = fullDateTimeText(at.toISOString())
    .replaceAll('-', '')
    .replaceAll(':', '')
    .replace(' ', '-')
  return `${safe}_${t('eventLog.fileTag')}_${stamp}.csv`
}
